/**
 * Copy coalescing — removes the `x = y` copies left by leaving SSA form.
 *
 * Out-of-SSA translation (PhiElimination) inserts `phiResult = input` at the end of each
 * predecessor of a merge. When the two sides are versions of the same wasm local the copy is a
 * self-assignment that lowering drops, but when a merge joins two different locals the copy
 * survives as `x = y` — readable noise (`var1 = ptr; … ptr = var1;`).
 *
 * Two variables connected by a copy can be merged into one — eliminating the copy — exactly when
 * they do not INTERFERE: there is no program point where both are live holding different values.
 * This pass computes liveness over the structured tree, builds the interference relation, and
 * coalesces each non-interfering copy (union-find), renaming the class to one name and dropping
 * the resulting self-copies. Genuine swaps (`a = b; … b = a`, where the two are simultaneously
 * live) interfere and are correctly left alone.
 *
 * Soundness first: a wrong coalesce changes behaviour. Liveness is recorded with the converged
 * (largest) live sets, and any function whose body uses constructs this pass does not model
 * exactly — switch, labels, or gotos — is skipped untouched.
 */

import { Expression, Statement, LoweredNode } from './ExpressionIR';

// Per-loop liveness fixpoints multiply through nested loops, so a huge deeply-nested function can
// be very expensive. Bound the total statement-visits and skip coalescing (leaving the copies)
// for any function that exceeds it — readability cleanup is never worth pathological slowdown.
const MAX_LIVENESS_WORK = 400_000;

class CoalescingBudgetExceeded extends Error {}

// Reset per function; incremented per statement visited during liveness (re-visited each loop
// fixpoint iteration), so it bounds the multiplicative cost of nested-loop fixpoints.
let livenessWork = 0;

export function coalesceCopies(node: LoweredNode, paramNames: Set<string>): LoweredNode {
  if (hasUnsupportedConstruct(node)) {
    return node;
  }
  const interference = new Map<string, Set<string>>();
  livenessWork = 0;
  try {
    analyze(node, new Set<string>(), null, interference, true);
  } catch (error) {
    if (error instanceof CoalescingBudgetExceeded) {
      return node;
    }
    throw error;
  }
  // Parameters are all live at entry holding distinct incoming values, so no two may merge; and a
  // parameter's name is bound by the signature, so it must be the representative of any class it
  // joins. Pairwise interference enforces both.
  addParameterInterference(paramNames, interference);
  const representatives = coalesce(collectCopies(node), interference, paramNames);
  if (representatives.size === 0) {
    return node;
  }
  return removeSelfCopies(renameNode(node, name => representatives.get(name) ?? name));
}

function addParameterInterference(paramNames: Set<string>, interference: Map<string, Set<string>>): void {
  const params = [...paramNames];
  for (let first = 0; first < params.length; first++) {
    for (let second = first + 1; second < params.length; second++) {
      addInterference(interference, params[first], params[second]);
    }
  }
}

// This pass models only the structured constructs whose control flow it can follow exactly.
// Labeled blocks/breaks and the goto fallback are left untouched so liveness is never
// under-approximated. (switch is modeled: each case exits to the switch's live-out.)
function hasUnsupportedConstruct(node: LoweredNode): boolean {
  switch (node.kind) {
    case 'labeled_block': case 'labeled_break': case 'labeled_continue': case 'label': case 'goto':
      return true;
    case 'sequence': return node.children.some(hasUnsupportedConstruct);
    case 'if': return hasUnsupportedConstruct(node.thenBody) || (node.elseBody !== null && hasUnsupportedConstruct(node.elseBody));
    case 'while': case 'do_while': return hasUnsupportedConstruct(node.body);
    case 'for': return hasUnsupportedConstruct(node.body);
    case 'switch': return node.cases.some(caseEntry => hasUnsupportedConstruct(caseEntry.body)) || hasUnsupportedConstruct(node.defaultBody);
    default: return false;
  }
}

// ─── liveness + interference ───

interface LoopContext {
  continueLive: Set<string>;
  breakLive: Set<string>;
  parent: LoopContext | null;
}

// Returns the set of variables live on entry to `node`, given those live on exit (`liveOut`).
// When `record` is set, interference edges are added at every definition using the final live
// sets — so it is run once, after the loop fixpoints below have converged.
function analyze(node: LoweredNode, liveOut: Set<string>, loop: LoopContext | null, interference: Map<string, Set<string>>, record: boolean): Set<string> {
  switch (node.kind) {
    case 'block': return analyzeBlock(node.body, liveOut, interference, record);
    case 'sequence': {
      let live = liveOut;
      for (let index = node.children.length - 1; index >= 0; index--) {
        live = analyze(node.children[index], live, loop, interference, record);
      }
      return live;
    }
    case 'if': {
      const thenLive = analyze(node.thenBody, liveOut, loop, interference, record);
      const elseLive = node.elseBody ? analyze(node.elseBody, liveOut, loop, interference, record) : liveOut;
      return union(expressionUses(node.condition), thenLive, elseLive);
    }
    case 'while': return analyzeWhile(node, liveOut, loop, interference, record);
    case 'do_while': return analyzeDoWhile(node, liveOut, loop, interference, record);
    case 'for': return analyzeFor(node, liveOut, loop, interference, record);
    case 'switch': {
      // Each case exits to the switch's live-out (an explicit `break` node or the emitter-added
      // one). A `break` inside a case targets the switch; a `continue` skips to the enclosing loop,
      // so its continue set is inherited unchanged.
      const context: LoopContext = { breakLive: liveOut, continueLive: loop ? loop.continueLive : new Set<string>(), parent: loop };
      const caseLiveIns = node.cases.map(caseEntry => analyze(caseEntry.body, liveOut, context, interference, record));
      const defaultLive = analyze(node.defaultBody, liveOut, context, interference, record);
      return union(expressionUses(node.selector), ...caseLiveIns, defaultLive);
    }
    case 'return': return collectExpressionVars(node.values);
    case 'break': return loop ? loop.breakLive : liveOut;
    case 'continue': return loop ? loop.continueLive : liveOut;
    default: return liveOut;
  }
}

function analyzeBlock(body: Statement[], liveOut: Set<string>, interference: Map<string, Set<string>>, record: boolean): Set<string> {
  livenessWork += body.length;
  if (livenessWork > MAX_LIVENESS_WORK) {
    throw new CoalescingBudgetExceeded();
  }
  let live = new Set(liveOut);
  for (let index = body.length - 1; index >= 0; index--) {
    const statement = body[index];
    if (record) {
      recordStatementInterference(statement, live, interference);
    }
    live = transfer(statement, live);
  }
  return live;
}

function analyzeWhile(node: LoweredNode & { kind: 'while' }, liveOut: Set<string>, loop: LoopContext | null, interference: Map<string, Set<string>>, record: boolean): Set<string> {
  const conditionUses = node.condition ? expressionUses(node.condition) : new Set<string>();
  let liveIn = new Set(liveOut);
  for (;;) {
    const context: LoopContext = { continueLive: liveIn, breakLive: liveOut, parent: loop };
    const bodyIn = analyze(node.body, liveIn, context, interference, false);
    const next = union(conditionUses, bodyIn, liveOut);
    if (sameSet(next, liveIn)) {
      break;
    }
    liveIn = next;
  }
  if (record) {
    analyze(node.body, liveIn, { continueLive: liveIn, breakLive: liveOut, parent: loop }, interference, true);
  }
  return liveIn;
}

function analyzeDoWhile(node: LoweredNode & { kind: 'do_while' }, liveOut: Set<string>, loop: LoopContext | null, interference: Map<string, Set<string>>, record: boolean): Set<string> {
  const conditionUses = expressionUses(node.condition);
  let liveIn = new Set(liveOut);
  for (;;) {
    const testLive = union(conditionUses, liveIn, liveOut);
    const context: LoopContext = { continueLive: testLive, breakLive: liveOut, parent: loop };
    const bodyIn = analyze(node.body, testLive, context, interference, false);
    if (sameSet(bodyIn, liveIn)) {
      break;
    }
    liveIn = bodyIn;
  }
  if (record) {
    const testLive = union(conditionUses, liveIn, liveOut);
    analyze(node.body, testLive, { continueLive: testLive, breakLive: liveOut, parent: loop }, interference, true);
  }
  return liveIn;
}

function analyzeFor(node: LoweredNode & { kind: 'for' }, liveOut: Set<string>, loop: LoopContext | null, interference: Map<string, Set<string>>, record: boolean): Set<string> {
  // for (init; cond; incr) body  ≡  init; while (cond) { body; incr }, with continue → incr.
  const conditionUses = expressionUses(node.condition);
  let testLive = new Set(liveOut);
  for (;;) {
    const incrIn = transfer(node.increment, testLive);
    const context: LoopContext = { continueLive: incrIn, breakLive: liveOut, parent: loop };
    const bodyIn = analyze(node.body, incrIn, context, interference, false);
    const next = union(conditionUses, bodyIn, liveOut);
    if (sameSet(next, testLive)) {
      break;
    }
    testLive = next;
  }
  const incrIn = transfer(node.increment, testLive);
  if (record) {
    recordStatementInterference(node.increment, testLive, interference);
    analyze(node.body, incrIn, { continueLive: incrIn, breakLive: liveOut, parent: loop }, interference, true);
    // init runs once and flows to the first condition test, so its live-out is that test's set.
    recordStatementInterference(node.init, testLive, interference);
  }
  return transfer(node.init, testLive);
}

// live before the statement = (live after − defined) ∪ used.
function transfer(statement: Statement, liveAfter: Set<string>): Set<string> {
  const live = new Set(liveAfter);
  const defined = statementDef(statement);
  if (defined !== null) {
    live.delete(defined);
  }
  for (const used of statementUses(statement)) {
    live.add(used);
  }
  return live;
}

// At a definition `v = …`, v interferes with everything live just after it — except itself and,
// when the definition is the copy `v = w`, the copied-from `w` (they are equal there, the whole
// point of coalescing).
function recordStatementInterference(statement: Statement, liveAfter: Set<string>, interference: Map<string, Set<string>>): void {
  const defined = statementDef(statement);
  if (defined === null) {
    return;
  }
  const copySource = copyOf(statement);
  for (const live of liveAfter) {
    if (live !== defined && live !== copySource) {
      addInterference(interference, defined, live);
    }
  }
}

function addInterference(interference: Map<string, Set<string>>, first: string, second: string): void {
  edgeSet(interference, first).add(second);
  edgeSet(interference, second).add(first);
}

function edgeSet(interference: Map<string, Set<string>>, name: string): Set<string> {
  let set = interference.get(name);
  if (!set) {
    set = new Set<string>();
    interference.set(name, set);
  }
  return set;
}

// ─── coalescing ───

interface Copy {
  target: string;
  source: string;
}

function coalesce(copies: Copy[], interference: Map<string, Set<string>>, paramNames: Set<string>): Map<string, string> {
  const parent = new Map<string, string>();
  const members = new Map<string, string[]>();
  const find = (name: string): string => {
    let root = name;
    while ((parent.get(root) ?? root) !== root) {
      root = parent.get(root)!;
    }
    let current = name;
    while (current !== root) {
      const next = parent.get(current)!;
      parent.set(current, root);
      current = next;
    }
    return root;
  };
  const ensure = (name: string): void => {
    if (!parent.has(name)) {
      parent.set(name, name);
      members.set(name, [name]);
    }
  };
  const classesInterfere = (firstRoot: string, secondRoot: string): boolean => {
    for (const x of members.get(firstRoot)!) {
      const neighbours = interference.get(x);
      if (!neighbours) { continue; }
      for (const y of members.get(secondRoot)!) {
        if (neighbours.has(y)) {
          return true;
        }
      }
    }
    return false;
  };

  let coalesced = false;
  for (const copy of copies) {
    ensure(copy.target);
    ensure(copy.source);
    const targetRoot = find(copy.target);
    const sourceRoot = find(copy.source);
    if (targetRoot === sourceRoot || classesInterfere(targetRoot, sourceRoot)) {
      continue;
    }
    const merged = members.get(targetRoot)!.concat(members.get(sourceRoot)!);
    parent.set(sourceRoot, targetRoot);
    members.set(targetRoot, merged);
    members.delete(sourceRoot);
    coalesced = true;
  }
  if (!coalesced) {
    return new Map();
  }

  const representatives = new Map<string, string>();
  for (const group of members.values()) {
    if (group.length <= 1) { continue; }
    const name = chooseName(group, paramNames);
    for (const member of group) {
      if (member !== name) {
        representatives.set(member, name);
      }
    }
  }
  return representatives;
}

// The representative kept for a merged class: a parameter must win (its name is signature-bound),
// then a meaningful (non-generated) name, then a stable alphabetical choice.
function chooseName(group: string[], paramNames: Set<string>): string {
  return [...group].sort((first, second) => {
    const firstRank = nameRank(first, paramNames);
    const secondRank = nameRank(second, paramNames);
    if (firstRank !== secondRank) {
      return firstRank - secondRank;
    }
    return first < second ? -1 : first > second ? 1 : 0;
  })[0];
}

function nameRank(name: string, paramNames: Set<string>): number {
  if (paramNames.has(name)) { return 0; }
  return isGenerated(name) ? 2 : 1;
}

function isGenerated(name: string): boolean {
  return /^var\d+$/.test(name) || /^global_\d+$/.test(name);
}

function collectCopies(node: LoweredNode): Copy[] {
  const copies: Copy[] = [];
  forEachStatement(node, statement => {
    const source = copyOf(statement);
    if (statement.kind === 'assign' && source !== null && source !== statement.target) {
      copies.push({ target: statement.target, source });
    }
  });
  return copies;
}

// ─── statement def / use ───

function statementDef(statement: Statement): string | null {
  if (statement.kind === 'assign') {
    return statement.target;
  }
  if ((statement.kind === 'call' || statement.kind === 'call_indirect') && statement.result) {
    return statement.result;
  }
  return null;
}

// The source variable when the statement is a plain copy `target = sourceVar`, else null.
function copyOf(statement: Statement): string | null {
  if (statement.kind === 'assign' && statement.value.kind === 'var') {
    return statement.value.name;
  }
  return null;
}

function statementUses(statement: Statement): Set<string> {
  switch (statement.kind) {
    case 'assign': return expressionUses(statement.value);
    case 'store': return union(expressionUses(statement.address), expressionUses(statement.value));
    case 'call': return collectExpressionVars(statement.args);
    case 'call_indirect': return union(expressionUses(statement.tableIndex), collectExpressionVars(statement.args));
    case 'global_set': return expressionUses(statement.value);
    case 'return': return collectExpressionVars(statement.values);
    case 'expr': return expressionUses(statement.value);
    default: return new Set<string>();
  }
}

function expressionUses(expr: Expression): Set<string> {
  const uses = new Set<string>();
  const visit = (current: Expression): void => {
    if (current.kind === 'var') {
      uses.add(current.name);
      return;
    }
    childExpressions(current).forEach(visit);
  };
  visit(expr);
  return uses;
}

function collectExpressionVars(expressions: Expression[]): Set<string> {
  const uses = new Set<string>();
  for (const expr of expressions) {
    for (const name of expressionUses(expr)) {
      uses.add(name);
    }
  }
  return uses;
}

// ─── set helpers ───

function union(...sets: Set<string>[]): Set<string> {
  const result = new Set<string>();
  for (const set of sets) {
    for (const value of set) {
      result.add(value);
    }
  }
  return result;
}

function sameSet(first: Set<string>, second: Set<string>): boolean {
  if (first.size !== second.size) {
    return false;
  }
  for (const value of first) {
    if (!second.has(value)) {
      return false;
    }
  }
  return true;
}

// ─── rename + self-copy removal ───

function removeSelfCopies(node: LoweredNode): LoweredNode {
  switch (node.kind) {
    case 'sequence': return { kind: 'sequence', children: node.children.map(removeSelfCopies) };
    case 'block': return { kind: 'block', body: node.body.filter(statement => !isSelfCopy(statement)) };
    case 'if': return { kind: 'if', condition: node.condition, thenBody: removeSelfCopies(node.thenBody), elseBody: node.elseBody ? removeSelfCopies(node.elseBody) : null };
    case 'while': return { kind: 'while', condition: node.condition, body: removeSelfCopies(node.body) };
    case 'do_while': return { kind: 'do_while', body: removeSelfCopies(node.body), condition: node.condition };
    case 'for': return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: removeSelfCopies(node.body) };
    case 'switch': return { kind: 'switch', selector: node.selector, cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: removeSelfCopies(caseEntry.body) })), defaultBody: removeSelfCopies(node.defaultBody) };
    default: return node;
  }
}

function isSelfCopy(statement: Statement): boolean {
  return statement.kind === 'assign' && statement.value.kind === 'var' && statement.value.name === statement.target;
}

function renameNode(node: LoweredNode, rename: (name: string) => string): LoweredNode {
  const mapStatement = (statement: Statement): Statement => renameStatement(statement, rename);
  const mapExpression = (expr: Expression): Expression => renameExpression(expr, rename);
  switch (node.kind) {
    case 'sequence': return { kind: 'sequence', children: node.children.map(child => renameNode(child, rename)) };
    case 'block': return { kind: 'block', body: node.body.map(mapStatement) };
    case 'if': return { kind: 'if', condition: mapExpression(node.condition), thenBody: renameNode(node.thenBody, rename), elseBody: node.elseBody ? renameNode(node.elseBody, rename) : null };
    case 'while': return { kind: 'while', condition: node.condition ? mapExpression(node.condition) : null, body: renameNode(node.body, rename) };
    case 'do_while': return { kind: 'do_while', body: renameNode(node.body, rename), condition: mapExpression(node.condition) };
    case 'for': return { kind: 'for', init: mapStatement(node.init), condition: mapExpression(node.condition), increment: mapStatement(node.increment), body: renameNode(node.body, rename) };
    case 'switch': return { kind: 'switch', selector: mapExpression(node.selector), cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: renameNode(caseEntry.body, rename) })), defaultBody: renameNode(node.defaultBody, rename) };
    case 'return': return { kind: 'return', values: node.values.map(mapExpression) };
    default: return node;
  }
}

function renameStatement(statement: Statement, rename: (name: string) => string): Statement {
  const mapExpression = (expr: Expression): Expression => renameExpression(expr, rename);
  switch (statement.kind) {
    case 'assign': return { kind: 'assign', target: rename(statement.target), type: statement.type, value: mapExpression(statement.value) };
    case 'store': return { kind: 'store', address: mapExpression(statement.address), offset: statement.offset, storeType: statement.storeType, value: mapExpression(statement.value) };
    case 'call': return { kind: 'call', name: statement.name, args: statement.args.map(mapExpression), result: statement.result === null ? null : rename(statement.result) };
    case 'call_indirect': return { kind: 'call_indirect', tableIndex: mapExpression(statement.tableIndex), args: statement.args.map(mapExpression), result: statement.result === null ? null : rename(statement.result) };
    case 'global_set': return { kind: 'global_set', name: statement.name, value: mapExpression(statement.value) };
    case 'return': return { kind: 'return', values: statement.values.map(mapExpression) };
    case 'expr': return { kind: 'expr', value: mapExpression(statement.value) };
    default: return statement;
  }
}

function renameExpression(expr: Expression, rename: (name: string) => string): Expression {
  if (expr.kind === 'var') {
    return { kind: 'var', name: rename(expr.name), type: expr.type };
  }
  return rebuildExpression(expr, child => renameExpression(child, rename));
}

// ─── generic tree walks ───

function forEachStatement(node: LoweredNode, visit: (statement: Statement) => void): void {
  switch (node.kind) {
    case 'sequence': node.children.forEach(child => forEachStatement(child, visit)); break;
    case 'block': node.body.forEach(visit); break;
    case 'if': forEachStatement(node.thenBody, visit); if (node.elseBody) { forEachStatement(node.elseBody, visit); } break;
    case 'while': case 'do_while': forEachStatement(node.body, visit); break;
    case 'for': visit(node.init); visit(node.increment); forEachStatement(node.body, visit); break;
    case 'switch': node.cases.forEach(caseEntry => forEachStatement(caseEntry.body, visit)); forEachStatement(node.defaultBody, visit); break;
  }
}

function childExpressions(expr: Expression): Expression[] {
  switch (expr.kind) {
    case 'binary': case 'compare': return [expr.left, expr.right];
    case 'unary': case 'convert': return [expr.operand];
    case 'load': return [expr.address];
    case 'select': return [expr.condition, expr.trueVal, expr.falseVal];
    case 'call': return expr.args;
    case 'call_indirect': return [expr.tableIndex, ...expr.args];
    case 'field_access': return [expr.base];
    case 'frame_slot': return [expr.index];
    default: return [];
  }
}

function rebuildExpression(expr: Expression, map: (child: Expression) => Expression): Expression {
  switch (expr.kind) {
    case 'binary': return { kind: 'binary', op: expr.op, left: map(expr.left), right: map(expr.right) };
    case 'compare': return { kind: 'compare', op: expr.op, left: map(expr.left), right: map(expr.right) };
    case 'unary': return { kind: 'unary', op: expr.op, operand: map(expr.operand) };
    case 'convert': return { kind: 'convert', op: expr.op, operand: map(expr.operand) };
    case 'select': return { kind: 'select', condition: map(expr.condition), trueVal: map(expr.trueVal), falseVal: map(expr.falseVal) };
    case 'load': return { kind: 'load', address: map(expr.address), offset: expr.offset, loadType: expr.loadType };
    case 'call': return { kind: 'call', name: expr.name, args: expr.args.map(map) };
    case 'call_indirect': return { kind: 'call_indirect', tableIndex: map(expr.tableIndex), args: expr.args.map(map) };
    case 'field_access': return { kind: 'field_access', base: map(expr.base), offset: expr.offset };
    case 'frame_slot': return { kind: 'frame_slot', index: map(expr.index) };
    default: return expr;
  }
}
